//modules
import React from 'react';
import { gsap } from "gsap";
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { CSSRulePlugin } from 'gsap/CSSRulePlugin';
import { NavigationBar } from '../globalComponents/NavigationBar';
import { ProjectsFooter } from '../globalComponents/ProjectsFooter';
import { useImageAnimation } from '../globalComponents/UseImageAnimation';
import { useTextAnimation } from '../globalComponents/UseTextAnimation';
import { UpButton } from '../globalComponents/UpButton'

//styles
import figmatocode_0 from '../images/figmatocode_0.webp';
import figmatocode_1 from '../images/figmatocode_1.webp';
import figmatocode_2 from '../images/figmatocode_2.webp';
import figmatocode_3 from '../images/figmatocode_3.webp';
import figmatocode_4 from '../images/figmatocode_4.webp';
import project_ticketsocket from '../images/project_ticketsocket.webp';
import project_vintach from '../images/project_vintach.webp';
import project_aptia from '../images/project_aptia.webp';
import doldrumdrifters from '../images/doldrumdrifters.webp'

// plugins
gsap.registerPlugin(CSSRulePlugin, ScrollTrigger);

export const FigmaToCode = ({ className }) => {

const StuffData = [
  ["Unity Design System", "White label design system for a benefits administration company.", project_aptia, "/Projects/DesignSystem"],
  ["Ticketing Ecosystem", "Product Ideation & UI Design for a ticketing ecosystem.", project_ticketsocket, "/Projects/Ticketing"],
  ["Vintage Marketplace", "Branding, Product Ideation & UI Design for a second-hand clothing marketplace.", project_vintach, "/Projects/Vintach"],
  ['Other Stuff', 'Personal projects covering illustration, code experiments and more.', doldrumdrifters, '/OtherStuff'],
];

const { containerRefs, imageRefs } = useImageAnimation(5);
const { textRef, headingRef, subtitleRef, bodyRef } = useTextAnimation();

    return(
        <div>
            <div>
            <NavigationBar />

            <div className="subpage-heading" ref={headingRef}>
                <h2 className="heading-2">
                    Figma to Code.
                </h2>
                <div className="subpage-subtitle">
                <p className="subtitle" ref={(el) => (subtitleRef.current[0] = el)}>Responsabilities</p>
                <p className="subtitle-bold" ref={(el) => (subtitleRef.current[1] = el)}>Design, Product & Documentation</p>
                </div>
            </div>

            <div className="project-body">
                <h5 className="heading-5" ref={(el) => (bodyRef.current[0] = el)}>/ Context</h5>
                <p className="project-notes body" ref={(el) => (bodyRef.current[1] = el)}>As a product designer and UI designer, I contributed to building a Figma to Code ecosystem that would improve significantly the hand-off process of one of the largest entertainment companies. This ecosystem aimed to streamline an automated process for exporting Figma designs into functional server driven UI.</p>
            </div>

            {/* Image 0 */}
            <section className="main">
              <div ref={containerRefs.current[0]} className="container">
                  <div className="img-container">
                      <img className="single-img" ref={imageRefs.current[0]} src={figmatocode_0} alt="Project Visual 0" />
                  </div>
              </div>
            </section>

            <div className="project-body">
                <h5 className="heading-5" ref={(el) => (textRef.current[0] = el)}>/ The Problem</h5>
                <p className="project-notes body" ref={(el) => (textRef.current[1] = el)}>The entertainment company faced a major challenge: streamlining the design-to-market process. The industry demands rapid iteration and continuous feature updates, but the existing workflow was too slow. Each requirement had to pass through multiple stages: analysis, research, design, development, QA, and deployment, leading to long turnaround times and bottlenecks. Our task was to find a solution that would accelerate this process without compromising quality.</p>
            </div>

              {/* Section Title */}
              <div className="project-body">
                  <h3 className="heading-3 section-heading" ref={(el) => (textRef.current[2] = el)}> Figma Plugin. </h3>
              </div>

              {/* Content */}
              <div className="project-body">
                  <h5 className="heading-5" ref={(el) => (textRef.current[3] = el)}>/ Design Export</h5>
                  <p className="project-notes body" ref={(el) => (textRef.current[4] = el)}>As part of this initiative, I designed a Figma plugin that streamlines the handoff between designers and developers. The plugin enables users to export well-structured Figma screens or components with a single click, generating code that serves as the foundation for further transformation into SDUI.</p>
              </div>

              {/* Image 1 */}
              <section className="main">
                <div ref={containerRefs.current[1]} className="container">
                    <div className="img-container">
                        <img className="single-img" ref={imageRefs.current[1]} src={figmatocode_1} alt="Project Visual 1" />
                    </div>
                </div>
              </section>

              {/* Content */}
              <div className="project-body">
                  <h5 className="heading-5" ref={(el) => (textRef.current[5] = el)}>/ Design Validation</h5>
                  <p className="project-notes body" ref={(el) => (textRef.current[6] = el)}>To ensure designs were properly structured for export, a validation system was developed within the Figma plugin. This validator checked designs against predefined rules, ensuring compatibility with the tool by enforcing proper layouts, design system compliance, and best practices. If issues were detected, the plugin provided detailed feedback with step-by-step instructions for resolving them directly in Figma. Documentation was also provided to further streamline the process, outlining common errors and their fixes.</p>
              </div>

              {/* Image 2 */}
              <section className="main">
                <div ref={containerRefs.current[2]} className="container">
                    <div className="img-container">
                        <img className="single-img" ref={imageRefs.current[2]} src={figmatocode_2} alt="Project Visual 2" />
                    </div>
                </div>
              </section>

              {/* Image 3 */}
              <section className="main">
                <div ref={containerRefs.current[3]} className="container">
                    <div className="img-container">
                        <img className="single-img" ref={imageRefs.current[3]} src={figmatocode_3} alt="Project Visual 3" />
                    </div>
                </div>
              </section>

              {/* Section Title */}
              <div className="project-body">
                  <h3 className="heading-3 section-heading" ref={(el) => (textRef.current[7] = el)}> Post Processing. </h3>
              </div>

              {/* Content */}
              <div className="project-body">
                  <h5 className="heading-5" ref={(el) => (textRef.current[8] = el)}> / Challenge</h5>
                  <p className="project-notes body" ref={(el) => (textRef.current[9] = el)}>As the plugin became adopted into the workflows of designers and developers, a major pain point emerged: making edits after export was frustrating and inefficient. The generated code structure was not optimized for large-scale adjustments, making post-processing tedious and demotivating for developers.</p>
              </div>

              {/* Content */}
              <div className="project-body">
                  <h5 className="heading-5" ref={(el) => (textRef.current[10] = el)}> / Post-processing</h5>
                  <p className="project-notes body" ref={(el) => (textRef.current[11] = el)}>To address this pain point, I designed a proof of concept for a low-code platform that would streamline post-processing. This platform provided an intuitive interface for refining the exported code, enabling tasks such as action definition, structural edits, and bug fixing. </p>
              </div>

              {/* Image 4 */}
              <section className="main">
                <div ref={containerRefs.current[4]} className="container">
                    <div className="img-container">
                        <img className="single-img" ref={imageRefs.current[4]} src={figmatocode_4} alt="Project Visual 4" />
                    </div>
                </div>
              </section>

              {/* Content */}
              <div className="last-body">
                  <h5 className="heading-5" ref={(el) => (textRef.current[12] = el)}> / Takeaways</h5>
                  <p className="project-notes body" ref={(el) => (textRef.current[13] = el)}>This project was a pivotal learning experience, deepening my understanding of designing for highly technical users. Tackling complex workflows required not only thoughtful UX solutions but also close collaboration with cross-functional teams. Through strategic communication and problem-solving, we built an ecosystem that significantly improved efficiency and streamlined development processes, proving that even the most intricate challenges can be simplified with the right approach. </p>
              </div>

            <ProjectsFooter StuffData={StuffData} />

        </div>
        <UpButton />
        </div>
    )
};